import { getFunctions, httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import app from '@/firebase';

const functions = getFunctions(app, 'us-central1');

export type OnboardingCopilotMessage = { role: 'user' | 'assistant'; content: string };
export type OnboardingCopilotOption = {
  id: string;
  label: string;
  action: 'select_step' | 'set_blocks_count' | 'set_block_layout' | 'set_value';
  payload: {
    step?: string;
    blockCount?: '1' | '2' | '3' | '5+' | '10+';
    floors?: number;
    unitsPerFloor?: number;
    key?: string;
    value?: string | number;
    openSummary?: boolean;
  };
};

export type OnboardingCopilotInputRequest = {
  id: string;
  label: string;
  fieldKey: string;
  type: 'string' | 'number' | 'enum';
  required: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  options?: string[];
};

export type OnboardingCopilotContext = {
  step: string;
  progress: number;
  stepLabel: string;
  stepPrompt: string;
  userName?: string;
  workspaceName?: string;
  workspaceLanguage?: 'en' | 'es' | 'pt';
  firstGreetingNeeded?: boolean;
  flowData?: Record<string, unknown>;
};

export async function callOnboardingCopilot(params: {
  messages: OnboardingCopilotMessage[];
  context: OnboardingCopilotContext;
}): Promise<{
  reply: string;
  options: OnboardingCopilotOption[];
  inputRequests: OnboardingCopilotInputRequest[];
}> {
  const fn = httpsCallable<
    { messages: OnboardingCopilotMessage[]; context: OnboardingCopilotContext },
    {
      reply: string;
      options?: OnboardingCopilotOption[];
      inputRequests?: OnboardingCopilotInputRequest[];
    }
  >(functions, 'onboardingCopilotChat');

  const result: HttpsCallableResult<{
    reply: string;
    options?: OnboardingCopilotOption[];
    inputRequests?: OnboardingCopilotInputRequest[];
  }> = await fn(params);
  const reply = result.data?.reply;
  if (typeof reply !== 'string' || !reply.trim()) {
    throw new Error('Invalid copilot response');
  }
  const options = Array.isArray(result.data?.options) ? result.data.options : [];
  const inputRequests = Array.isArray(result.data?.inputRequests)
    ? result.data.inputRequests
    : [];
  return { reply: reply.trim(), options, inputRequests };
}
